import React, { useEffect, useState } from "react";
import {
  Table,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  IconButton,
  Switch,
  CircularProgress,
  MenuItem,
  Button,
  Typography,
} from "@mui/material";

import styles from "./hoverableStyles.module.css";
import { useTranslation } from "react-i18next";

import { formatDate, stringIndexTrim } from "../../helpers/StringUtils";
import ReactFileReader from "react-file-reader";
import { SubsystemFirmwareTypes } from "../../enums/SubsystemFirmwareTypes";

import {
  Publish as UploadIcon,
  Delete as DeleteIcon,
  GetApp as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { useBackend } from "../../context/BackendContext";
import { LanguageConverter } from "../../enums/LanguageConverter";
import {
  DialogActionTypes,
  useDialogContext,
} from "../../context/DialogContext";

export default function SubsystemFirmwaresTable({
  subsystemId,
  subsystemTypeId,
  subsystemName,
  parentId,
  isCanSupported,
  carCode,
  flatMode,
  publicMode,
}) {
  const { t, i18n } = useTranslation();
  const dialogDispatch = useDialogContext();
  const backend = useBackend();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  const changePublishStatus = (firmware, value) => {
    backend
      .bckDevicesCreateOrUpdateSubsystemFirmware({
        id: firmware.id,
        isPublished: value,
      })
      .then((json) => {
        if (json.code === 0) {
          setData((prev) => {
            let next = [...prev];
            let target = next.find((r) => r.id === firmware.id);
            target.isPublished = value;
            target.publishTime = new Date();
            return next;
          });
        }
      });
  };

  const handlePublishChanged = (firmware, value) => {
    dialogDispatch({
      type: DialogActionTypes.ConfirmationDialogOpen,
      userMessage: t("Change publication status?"),
      handleConfirm: () => {
        changePublishStatus(firmware, value);
      },
    });
  };

  const removeFirmware = (firmware) => {
    backend.bckDevicesRemoveSubsystemFirmware(firmware.id).then((json) => {
      if (json.code === 0) {
        setData((prev) => {
          let next = [...prev];
          let foundIndex = next.findIndex((x) => x.id === firmware.id);
          if (foundIndex !== -1) {
            next.splice(foundIndex, 1);
          }
          return next;
        });
      }
    });
  };

  const handleRemoveFirmware = (firmware) => {
    dialogDispatch({
      type: DialogActionTypes.ConfirmationDialogOpen,
      userMessage: t("Delete firmware?"),
      handleConfirm: () => {
        removeFirmware(firmware);
      },
    });
  };

  const handleRemoveAll = () => {
    dialogDispatch({
      type: DialogActionTypes.ConfirmationDialogOpen,
      userMessage: t("Delete all firmware?"),
      handleConfirm: () => {
        let dataCopy = [...data];
        dataCopy.forEach((f) => {
          removeFirmware(f);
        });
      },
    });
  };

  const handlePublishAll = () => {
    dialogDispatch({
      type: DialogActionTypes.ConfirmationDialogOpen,
      userMessage: t("Publish all firmware?"),
      handleConfirm: () => {
        let dataCopy = [...data];
        dataCopy.forEach((f) => {
          changePublishStatus(f, true);
        });
      },
    });
  };

  const handleUnpublishAll = () => {
    dialogDispatch({
      type: DialogActionTypes.ConfirmationDialogOpen,
      userMessage: t("Unpublish all firmware?"),
      handleConfirm: () => {
        let dataCopy = [...data];
        dataCopy.forEach((f) => {
          changePublishStatus(f, false);
        });
      },
    });
  };

  const uploadFirmwareWithCAN = (file) => {
    backend
      .bckDevicesCreateOrUpdateSubsystemFirmware({
        subsystemId: subsystemId,
        parentId: parentId,
        isPublished: false,
      })
      .then((json) => {
        if (json.code === 0) {
          let newRecord = {
            creationTime: new Date().toISOString(),
            filename: null,
            id: json.content,
            isPublished: false,
            publishTime: null,
            version: null,
            type: 3,
          };
          setData((prev) => {
            let next = [...prev];
            next.unshift(newRecord);
            return next;
          });
          handleUploadSelect(newRecord, file, true);
        }
      });
  };

  const handleUploadFirmwares = (files) => {
    if (!parentId) return;

    for (let i = 0; i < files.length; i++) {
      let file = files[i];
      uploadFirmwareWithCAN(file);
    }
  };

  const handleUploadSelect = (firmware, file, noDialogs) => {
    if (file.size > 512 * 1024 * 1024) {
      alert(t("File is too large"));
      return;
    }

    let extension = file.name.substring(file.name.lastIndexOf("."));
    if (extension === ".enc") {
      handleUploadFirmwareEnc(firmware, file, noDialogs);
    } else if (extension === ".pld") {
      handleUploadFirmwarePld(firmware, file, noDialogs);
    } else if (extension === ".mld") {
      handleUploadFirmwareMld(firmware, file, noDialogs);
    } else if (extension === ".fld") {
      handleUploadFirmwareFld(firmware, file, noDialogs);
    } else {
      handleUploadFirmwareUnknown(firmware, file, noDialogs);
    }
  };

  const handleUploadFirmwareEnc = (firmware, file, noDialogs) => {
    let blob = file.slice(0, 24);
    const reader = new FileReader();
    reader.addEventListener("load", (event) => {
      let header = event.target.result;
      let deviceName = stringIndexTrim(header.substring(0, 16).trim(), "\0");
      let version = stringIndexTrim(header.substring(16, 24).trim(), "\0");
      handleUploadFirmwareKnownFormat(
        firmware,
        file,
        version,
        deviceName,
        null,
        null,
        noDialogs,
      );
    });
    reader.readAsBinaryString(blob);
  };

  const handleUploadFirmwarePld = (firmware, file, noDialogs) => {
    let blob = file.slice(0, 24);
    const reader = new FileReader();
    reader.addEventListener("load", (event) => {
      let can = null;
      let canVersion = null;
      let deviceName = null;
      let header = event.target.result;
      let version = stringIndexTrim(header.substring(0, 8).trim(), "\0");

      if (isCanSupported) {
        deviceName = stringIndexTrim(header.substring(8, 20).trim(), "\0");

        let canBrand = header[20].charCodeAt(0);
        let canModel = header[21].charCodeAt(0);
        if (canBrand !== 32 || canModel !== 32) {
          let canBrandHex = canBrand
            .toString(16)
            .padStart(2, "0")
            .toUpperCase();
          let canModelHex = canModel
            .toString(16)
            .padStart(2, "0")
            .toUpperCase();
          can = "CAN_" + canBrandHex + canModelHex;
        }

        let canVersionByte1 = header[22].charCodeAt(0);
        let canVersionByte2 = header[23].charCodeAt(0);
        if (canVersionByte1 !== 32 || canVersionByte2 !== 32) {
          let canVersionInt = (canVersionByte2 << 8) + canVersionByte1;
          canVersion = "VER" + (canVersionInt / 100).toFixed(2);
        }
      } else {
        deviceName = stringIndexTrim(header.substring(8, 24).trim(), "\0");
      }

      handleUploadFirmwareKnownFormat(
        firmware,
        file,
        version,
        deviceName,
        can,
        canVersion,
        noDialogs,
      );
    });
    reader.readAsBinaryString(blob);
  };

  const handleUploadFirmwareMld = (firmware, file, noDialogs) => {
    let blob = file.slice(0, 21);
    const reader = new FileReader();
    reader.addEventListener("load", (event) => {
      let header = event.target.result;
      let version = stringIndexTrim(header.substring(1, 9).trim(), "\0");
      let deviceName = stringIndexTrim(header.substring(9, 21).trim(), "\0");
      handleUploadFirmwareKnownFormat(
        firmware,
        file,
        version,
        deviceName,
        null,
        null,
        noDialogs,
      );
    });
    reader.readAsBinaryString(blob);
  };

  const handleUploadFirmwareFld = (firmware, file, noDialogs) => {
    let blob = file.slice(0, 128);
    const reader = new FileReader();
    reader.onload = () => {
      let header = reader.result;
      let version = stringIndexTrim(header.substring(4, 12).trim(), "\0");
      let deviceName = stringIndexTrim(header.substring(12, 24).trim(), "\0");
      let sectionsCount = header.at(3).charCodeAt(0) >> 4;
      let canSectionStart = 4 + sectionsCount * 28;
      let canSectionContent = stringIndexTrim(
        header.substring(canSectionStart, canSectionStart + 64).trim(),
        "\0",
      );
      let can = "";
      let canVersion = "";
      if (canSectionContent.startsWith("CAN_")) {
        let tokens = canSectionContent.split(" ");
        can = tokens[0];
        canVersion = tokens[1];
      }

      handleUploadFirmwareKnownFormat(
        firmware,
        file,
        version,
        deviceName,
        can,
        canVersion,
        noDialogs,
      );
    };
    reader.readAsBinaryString(blob);
  };

  const handleUploadFirmwareKnownFormat = (
    firmware,
    file,
    version,
    deviceName,
    can,
    canVersion,
    noDialogs,
  ) => {
    if (!noDialogs && subsystemName !== deviceName) {
      dialogDispatch({
        type: DialogActionTypes.ConfirmationDialogOpen,
        userMessage: t("Firmware device name mismatch", {
          fileDeviceName: deviceName,
          currentDeviceName: subsystemName,
        }),
        handleConfirm: () => {
          handleUploadFirmwareConfirm(firmware, file, version, can, canVersion);
        },
      });
    } else if (!noDialogs && version !== firmware.version) {
      dialogDispatch({
        type: DialogActionTypes.ConfirmationDialogOpen,
        userMessage: t("Firmware version mismatch", {
          fileVersion: version,
          recordVersion: firmware.version,
        }),
        handleConfirm: () => {
          handleUploadFirmwareConfirm(firmware, file, version, can, canVersion);
        },
      });
    } else {
      handleUploadFirmwareConfirm(firmware, file, version, can, canVersion);
    }
  };

  const handleUploadFirmwareUnknown = (firmware, file, noDialogs) => {
    if (noDialogs) {
      handleUploadFirmwareConfirm(firmware, file, null, null);
    } else {
      dialogDispatch({
        type: DialogActionTypes.ConfirmationDialogOpen,
        userMessage: t("Upload firmware with unknown format?"),
        handleConfirm: () => {
          handleUploadFirmwareConfirm(firmware, file, null, null);
        },
      });
    }
  };

  const handleUploadFirmwareConfirm = (
    firmware,
    file,
    version,
    can,
    canVersion,
  ) => {
    backend.bckFilesUploadSubsystemFirmware(firmware.id, file).then((json) => {
      if (json.code === 0) {
        setData((prev) => {
          let next = [...prev];
          let target = next.find((r) => r.id === firmware.id);
          if (target) {
            let filename = decodeURI(
              json.content.substring(json.content.lastIndexOf("/") + 1),
            );
            target.url = json.content;
            target.filename = filename;
            if (version) {
              if (version.indexOf("VER") === 0) {
                version = version.substr(3);
              }
              updateFirmwareVersion(firmware, version);
            }
            if (can) {
              if (can.indexOf("CAN_") === 0) {
                can = can.substr(4).replaceAll("_", "");
              }
              updateFirmwareCan(firmware, can);
            }
            if (canVersion) {
              if (canVersion.indexOf("VER") === 0) {
                canVersion = canVersion.substr(3);
              }
              updateFirmwareCanVersion(firmware, canVersion);
            }
          }
          return next;
        });
      }
    });
  };

  const handleTypeClick = (firmware) => {
    dialogDispatch({
      type: DialogActionTypes.SimpleSelectDialogOpen,
      initialValue: firmware.type,
      label: t("Type"),
      title: t("Type"),
      menuItems: Object.keys(SubsystemFirmwareTypes).map((i) => (
        <MenuItem key={i} value={i}>
          {t(SubsystemFirmwareTypes[i])}
        </MenuItem>
      )),
      handleConfirm: (value) => {
        backend
          .bckDevicesCreateOrUpdateSubsystemFirmware({
            id: firmware.id,
            type: value,
          })
          .then((json) => {
            if (json.code === 0) {
              setData((prev) => {
                let next = [...prev];
                let target = next.find((r) => r.id === firmware.id);
                target.type = value;
                return next;
              });
            }
          });
      },
    });
  };

  const handleDownloadClick = (firmware) => {
    window.open(firmware.url, `_blank`);
  };

  useEffect(() => {
    if (!subsystemId && !subsystemTypeId) return;
    backend
      .bckDevicesListSubsystemFirmwares({
        subsystemId: subsystemId,
        subsystemType: subsystemTypeId,
        parentId: parentId,
        lang: LanguageConverter[i18n.language],
        includeNotPublished: publicMode ? false : true,
        onlyRoots: flatMode ? false : true,
        carCode: carCode,
        pageNumber: 1,
        pageSize: 1000,
      })
      .then((json) => {
        setIsLoading(false);
        if (json.code === 0) {
          setData(json.content.list);
        }
      });
  }, [
    subsystemId,
    subsystemTypeId,
    parentId,
    carCode,
    flatMode,
    publicMode,
    backend,
    setIsLoading,
    i18n.language,
  ]);

  const updateFirmwareVersion = (firmware, version) => {
    backend
      .bckDevicesCreateOrUpdateSubsystemFirmware({
        id: firmware.id,
        version: version,
      })
      .then((json) => {
        if (json.code === 0) {
          setData((prev) => {
            let next = [...prev];
            let target = next.find((r) => r.id === firmware.id);
            target.version = version;
            return next;
          });
        }
      });
  };

  const updateFirmwareCan = (firmware, can) => {
    backend
      .bckDevicesCreateOrUpdateSubsystemFirmware({
        id: firmware.id,
        carCode: can,
      })
      .then((json) => {
        if (json.code === 0) {
          setData((prev) => {
            let next = [...prev];
            let target = next.find((r) => r.id === firmware.id);
            target.carCode = can;
            return next;
          });
        }
      });
  };

  const updateFirmwareCanVersion = (firmware, canVersion) => {
    backend
      .bckDevicesCreateOrUpdateSubsystemFirmware({
        id: firmware.id,
        canVersion: canVersion,
      })
      .then((json) => {
        if (json.code === 0) {
          setData((prev) => {
            let next = [...prev];
            let target = next.find((r) => r.id === firmware.id);
            target.canVersion = canVersion;
            return next;
          });
        }
      });
  };

  const handleVersionClick = (firmware) => {
    dialogDispatch({
      type: DialogActionTypes.SimpleTextDialogOpen,
      initialValue: firmware.version,
      textLabel: t("Version"),
      title: t("Version"),
      maxLength: 9,
      allowEmptyOk: true,
      handleConfirm: (version) => {
        updateFirmwareVersion(firmware, version);
      },
    });
  };

  const handleCanVersionClick = (firmware) => {
    dialogDispatch({
      type: DialogActionTypes.SimpleTextDialogOpen,
      initialValue: firmware.canVersion,
      textLabel: t("CAN version"),
      title: t("CAN version"),
      maxLength: 9,
      allowEmptyOk: true,
      handleConfirm: (version) => {
        updateFirmwareCanVersion(firmware, version);
      },
    });
  };

  const handleDescriptionClick = (firmware) => {
    dialogDispatch({
      type: DialogActionTypes.SimpleTextDialogOpen,
      initialValue: firmware.description,
      textLabel: t("Description"),
      title: t("Description"),
      lines: 5,
      handleConfirm: (description) => {
        backend
          .bckDevicesCreateOrUpdateSubsystemFirmware({
            id: firmware.id,
            description: description,
            lang: LanguageConverter[i18n.language],
          })
          .then((json) => {
            if (json.code === 0) {
              setData((prev) => {
                let next = [...prev];
                let target = next.find((r) => r.id === firmware.id);
                target.description = description;
                return next;
              });
            }
          });
      },
    });
  };

  const handleCANClick = (firmware) => {
    dialogDispatch({
      type: DialogActionTypes.SimpleTextDialogOpen,
      initialValue: firmware.carCode,
      textLabel: t("CAN"),
      title: t("CAN"),
      allowEmptyOk: true,
      onChange: (e) => {
        let filteredValue = e.target.value.replace(/[^0-9]/g, "");
        return filteredValue.substr(0, 4);
      },
      handleConfirm: (newCan) => {
        updateFirmwareCan(firmware, newCan);
      },
    });
  };

  const handleAddFirmware = () => {
    dialogDispatch({
      type: DialogActionTypes.ConfirmationDialogOpen,
      userMessage: t("Add firmware") + "?",
      handleConfirm: () => {
        backend
          .bckDevicesCreateOrUpdateSubsystemFirmware({
            subsystemId: subsystemId,
            parentId: parentId,
            isPublished: false,
          })
          .then((json) => {
            if (json.code === 0) {
              setData((prev) => {
                let next = [...prev];
                next.unshift({
                  creationTime: new Date().toISOString(),
                  filename: null,
                  id: json.content,
                  isPublished: false,
                  publishTime: null,
                  version: null,
                  type: 3,
                });
                return next;
              });
            }
          });
      },
    });
  };

  let keys = publicMode
    ? [t("Filename"), t("Version"), t("CAN version"), t("Is published")]
    : [
        "",
        "ID",
        t("Filename"),
        t("Type"),
        t("CAN"),
        t("Version"),
        t("CAN version"),
        t("Is published"),
        t("Creation time"),
        t("Description"),
      ];

  if (isLoading) {
    return <CircularProgress />;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {publicMode ? (
        <></>
      ) : (
        <div>
          {parentId ? (
            <div style={{ display: "flex", flexDirection: "row" }}>
              <ReactFileReader
                handleFiles={(files) => handleUploadFirmwares(files)}
                multipleFiles
                fileTypes={[".pld", ".mld", ".enc", ".fld"]}
              >
                <Button
                  variant="contained"
                  color="primary"
                  style={{ margin: "8px" }}
                >
                  {t("Load firmwares")}
                </Button>
              </ReactFileReader>
              <Button
                onClick={handleRemoveAll}
                variant="contained"
                color="primary"
                style={{ margin: "8px" }}
              >
                {t("Delete all")}
              </Button>
              <Button
                onClick={handlePublishAll}
                variant="contained"
                color="primary"
                style={{ margin: "8px" }}
              >
                {t("Publish all")}
              </Button>
              <Button
                onClick={handleUnpublishAll}
                variant="contained"
                color="primary"
                style={{ margin: "8px" }}
              >
                {t("Unpublish all")}
              </Button>
            </div>
          ) : (
            <Button
              onClick={handleAddFirmware}
              variant="contained"
              color="primary"
              style={{ marginBottom: "16px" }}
            >
              {t("Add firmware")}
            </Button>
          )}
        </div>
      )}
      {data && data.length > 0 ? (
        <Table size="small" style={{ marginBottom: "16px" }}>
          <TableHead>
            <TableRow>
              {keys.map((key) => (
                <TableCell key={key} style={{ fontWeight: "bold" }}>
                  {key}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((firmware) => (
              <React.Fragment key={firmware.id}>
                <TableRow>
                  {publicMode ? (
                    <></>
                  ) : (
                    <TableCell>
                      {flatMode ? (
                        <></>
                      ) : (
                        <IconButton
                          disabled={parentId !== undefined}
                          onClick={() => {
                            if (expandedId === firmware.id) {
                              setExpandedId(null);
                            } else {
                              setExpandedId(firmware.id);
                            }
                          }}
                          color="primary"
                        >
                          {expandedId === firmware.id ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </IconButton>
                      )}
                      <IconButton
                        style={{ color: "red" }}
                        onClick={() => handleRemoveFirmware(firmware)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  )}
                  {publicMode ? (
                    <></>
                  ) : (
                    <TableCell className={styles.cell}>{firmware.id}</TableCell>
                  )}
                  <TableCell className={styles.cell}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {firmware.filename}
                      {publicMode ? (
                        <></>
                      ) : (
                        <ReactFileReader
                          handleFiles={(files) =>
                            handleUploadSelect(firmware, files[0], false)
                          }
                          fileTypes={[".pld", ".mld", ".enc", ".fld"]}
                        >
                          <IconButton color="primary">
                            <UploadIcon />
                          </IconButton>
                        </ReactFileReader>
                      )}
                      <IconButton
                        color="primary"
                        disabled={!firmware.filename}
                        onClick={() => handleDownloadClick(firmware)}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </div>
                  </TableCell>
                  {publicMode ? (
                    <></>
                  ) : (
                    <TableCell
                      className={styles.hoverableCell}
                      onClick={() => handleTypeClick(firmware)}
                    >
                      {SubsystemFirmwareTypes[firmware.type]}
                    </TableCell>
                  )}
                  {publicMode ? (
                    <></>
                  ) : (
                    <TableCell
                      className={styles.hoverableCell}
                      onClick={() => handleCANClick(firmware)}
                    >
                      {firmware.carCode}
                    </TableCell>
                  )}
                  <TableCell
                    className={publicMode ? styles.cell : styles.hoverableCell}
                    onClick={
                      publicMode ? null : () => handleVersionClick(firmware)
                    }
                  >
                    {firmware.version}
                  </TableCell>
                  <TableCell
                    className={publicMode ? styles.cell : styles.hoverableCell}
                    onClick={
                      publicMode ? null : () => handleCanVersionClick(firmware)
                    }
                  >
                    {firmware.canVersion}
                  </TableCell>
                  <TableCell className={styles.cell}>
                    {formatDate(firmware.publishTime)}
                    {publicMode ? (
                      <></>
                    ) : (
                      <Switch
                        color="primary"
                        checked={firmware.isPublished}
                        onChange={(e) =>
                          handlePublishChanged(firmware, e.target.checked)
                        }
                      />
                    )}
                  </TableCell>
                  {publicMode ? (
                    <></>
                  ) : (
                    <TableCell className={styles.cell}>
                      {formatDate(firmware.creationTime)}
                    </TableCell>
                  )}
                  {publicMode ? (
                    <></>
                  ) : (
                    <TableCell
                      className={styles.hoverableCell}
                      onClick={() => handleDescriptionClick(firmware)}
                    >
                      <pre
                        style={{
                          maxHeight: "7ch",
                          maxWidth: "64ch",
                          overflowX: "hidden",
                          overflowY: "hidden",
                        }}
                      >
                        {firmware.description}
                      </pre>
                    </TableCell>
                  )}
                </TableRow>
                {expandedId === firmware.id ? (
                  <TableRow key={firmware.id + "-inner-table"}>
                    <TableCell colSpan={keys.length}>
                      <SubsystemFirmwaresTable
                        subsystemId={subsystemId}
                        subsystemName={subsystemName}
                        parentId={firmware.id}
                        isCanSupported={isCanSupported}
                        flatMode={true}
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  <></>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography style={{ paddingBottom: "16px" }}>
          <b>{t("No firmwares")}</b>
        </Typography>
      )}
    </div>
  );
}
